import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../context/authContext';
import { optionsCategoria, optionsPartido } from '../datos/options';
import Select from 'react-select';

import { generarInformeOrganizacionesPdf } from '../utils/informeOrganizacionesPdf';

import encabezadoImg from '../components/informes/encabezado_A4.png';
import pieImg from '../components/informes/pie_hoja_A4.png';

// Formatea un número como moneda en pesos ($ 1.234.567,89)
const formatearMonto = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '$ 0,00';
    return '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatearEntero = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR');
};

// Monto conveniado total = monto_obra + adendas (misma convención que el informe por partido)
const getMontoConveniado = (obra) => Number(obra?.monto_obra || 0) + Number(obra?.adendas || 0);

const getTipoObra = (obra) => {
    const ts = obra?.array_tipo_subtipo_proyecto || [];
    if (Array.isArray(ts) && ts.length > 0) {
        return ts.map((item) => {
            const label = String(item?.subtipo || '').trim() || String(item?.tipo || '').trim() || 'Proyecto';
            const cantidad = Number(item?.cantidad || 0);
            return cantidad > 0 ? `${formatearEntero(cantidad)} ${label}` : label;
        });
    }
    if (obra?.area_responsable) return [obra.area_responsable];
    return ['—'];
};

const getUbicacion = (obra) => {
    const partido = String(obra?.partido_nombre || '').trim();
    const localidad = String(obra?.lc_21_localidad || '').trim();
    if (partido && localidad && partido.toLowerCase() !== localidad.toLowerCase()) {
        return `${partido} — ${localidad}`;
    }
    return partido || localidad || '—';
};

// Expediente del proyecto (objeto_o === "Proyecto"), igual que en los informes técnicos
const getExpedienteProyecto = (obra) => {
    const proyecto = (obra?.array_obras_expediente || []).find((item) => item && item.objeto_o === 'Proyecto');
    return proyecto?.expediente_o || 'Sin especificar';
};

// Escapa texto para insertarlo en el HTML que Excel va a abrir como .xls
const escaparHtml = (valor) =>
    String(valor ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/[\r\n]+/g, ' ');

// Contactos de la organización (subformulario "Contactos de la organización").
// Puede haber varios por ONG; se muestran como líneas en la tabla/PDF y
// unidos con "; " en el XLS.
const contactosOngPartes = (contactos) => {
    const lista = Array.isArray(contactos) ? contactos : [];
    return lista
        .map((c) => {
            const nombre = String(c?.nombre || '').trim();
            const tel = String(c?.telefono || '').trim();
            const email = String(c?.email || '').trim();
            const canales = [tel ? `Tel: ${tel}` : '', email].filter(Boolean).join(' · ');
            if (!nombre && !canales) return '';
            return canales ? `${nombre || 'Sin nombre'} (${canales})` : nombre;
        })
        .filter(Boolean);
};

const contactosOngTexto = (contactos) => contactosOngPartes(contactos).join('; ');

const TODAS_LAS_CATEGORIAS = optionsCategoria.map((c) => c.value).join(',');

// Años y gestiones: mismo patrón que los filtros de Tableros / Base Matriz.
const ALL_YEARS = ['2027','2026','2025','2024','2023','2022','2021','2020','2019','2018','2017','2016'];
const GESTIONES = [
    { key: '2da', label: '2da Gestión (2024–2027)', years: ['2027','2026','2025','2024'] },
    { key: '1era', label: '1era Gestión (2020–2023)', years: ['2023','2022','2021','2020'] },
];
const yearOptions = ALL_YEARS.map((y) => ({ value: y, label: y }));
const gestionOptions = GESTIONES.map((g) => ({ value: g.key, label: g.label }));
const CRITERIOS_ANIO = [
    { value: 'pago_anticipo', label: 'Pago (anticipo o total)' },
    { value: 'expediente', label: 'Actual (por año exp. de proyecto)' },
];

const InformeOrganizacionesPage = () => {
    const { token, role, roles } = useContext(AuthContext);

    const [categoria, setCategoria] = useState('');
    const [partido, setPartido] = useState('');
    const [q, setQ] = useState('');
    const [obras, setObras] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [generado, setGenerado] = useState(false);
    const [error, setError] = useState('');
    const [descargando, setDescargando] = useState(false);
    const [criterioYear, setCriterioYear] = useState('pago_anticipo');
    const [years, setYears] = useState([]);
    const [gestiones, setGestiones] = useState([]);
    const [soloSubtotales, setSoloSubtotales] = useState(false);

    const pdfRef = useRef(null);

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    // '' = todas las categorías; si no, la categoría elegida (por defecto religiosa)
    const categoriaLabel =
        categoria === '' ? 'Todas las categorías' : optionsCategoria.find((c) => c.value === categoria)?.label || categoria;

    const partidoLabel = partido ? optionsPartido.find((p) => p.value === partido)?.label || partido : '';

    const yearsLabel = years.length ? [...years].sort().join(', ') : '';

    const nombreArchivo = `informe_organizaciones_${categoria || 'todas'}${partidoLabel ? `_${partidoLabel}` : ''}${yearsLabel ? `_${yearsLabel}` : ''}${soloSubtotales ? '_subtotales' : ''}`.replace(/[^a-z0-9_]+/gi, '_');

    const selectedYearOptions = yearOptions.filter((o) => years.includes(o.value));
    const selectedGestionOptions = gestionOptions.filter((o) => gestiones.includes(o.value));

    const handleYearsChange = (selected) => {
        const valores = (selected || []).map((o) => o.value);
        setYears(valores);
        // La gestión se marca sola cuando están todos sus años elegidos.
        setGestiones(
            GESTIONES.filter((g) => valores.length > 0 && g.years.every((y) => valores.includes(y))).map((g) => g.key)
        );
    };

    const handleGestionChange = (selected) => {
        const keys = (selected || []).map((o) => o.value);
        setGestiones(keys);
        if (keys.length === 0) {
            setYears([]);
            return;
        }
        const elegidas = GESTIONES.filter((g) => keys.includes(g.key));
        setYears([...new Set(elegidas.flatMap((g) => g.years))].sort((a, b) => b.localeCompare(a)));
    };

    // Vuelve a los valores originales de la pantalla y regenera el informe.
    const restablecerFiltros = () => {
        setCategoria('');
        setPartido('');
        setQ('');
        setCriterioYear('pago_anticipo');
        setYears([]);
        setGestiones([]);
        setSoloSubtotales(false);
        cargar({ categoria: '', partido: '', q: '', years: [], criterioYear: 'pago_anticipo' });
    };

    const cargar = async (params) => {
        setCargando(true);
        setError('');
        setGenerado(false);
        try {
            const query = new URLSearchParams();
            query.set('categoria', params.categoria === '' ? TODAS_LAS_CATEGORIAS : params.categoria);
            if (params.partido) query.set('partido', params.partido);
            if (params.q) query.set('q', params.q);
            query.set('criterioYear', params.criterioYear ?? criterioYear);
            for (const y of (params.years || [])) query.append('year', y);
            const res = await fetch(`/api/informe_organizaciones?${query.toString()}`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // No mostrar obras en estado "En guarda" (misma convención que el informe por partido)
            const visibles = (data || []).filter((o) => String(o?.estado || '').trim().toLowerCase() !== 'en guarda');
            setObras(visibles);
            setGenerado(true);
            if (visibles.length === 0) {
                setError('No se encontraron obras para los filtros seleccionados.');
            }
        } catch (e) {
            console.error('Error al generar el informe de organizaciones:', e);
            setError(e.message);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar({ categoria: '', q: '', years: [], criterioYear: 'pago_anticipo' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadPDF = async () => {
        try {
            setDescargando(true);
            await generarInformeOrganizacionesPdf({
                grupos,
                totalInversion,
                cantidadObras: obras.length,
                cantidadOrganizaciones: grupos.length,
                categoriaLabel,
                partidoLabel,
                yearsLabel,
                q,
                soloSubtotales,
                nombreArchivo,
            });
        } catch (error) {
            console.error('Error al generar el PDF:', error);
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarXLS = () => {
        try {
            const encabezados = [
                'Organización',
                'Contacto ONG',
                'Categoría',
                'Terminal Política',
                ...(soloSubtotales ? [] : ['Exp. de Proyecto']),
                'Monto conveniado',
                'Adenda',
                'Total',
                ...(soloSubtotales ? [] : ['Tipo de obra', 'Ubicación']),
            ];

            const _xmlEscape = (v) => String(v)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');

            const _colLetter = (n) => {
                let s = '';
                let x = n + 1;
                while (x > 0) {
                    const rem = (x - 1) % 26;
                    s = String.fromCharCode(65 + rem) + s;
                    x = Math.floor((x - 1) / 26);
                }
                return s;
            };

            const _crc32 = (bytes) => {
                const table = _crc32._table || (_crc32._table = (() => {
                    const t = new Uint32Array(256);
                    for (let n = 0; n < 256; n++) {
                        let c = n;
                        for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
                        t[n] = c >>> 0;
                    }
                    return t;
                })());
                let c = 0xFFFFFFFF;
                for (let i = 0; i < bytes.length; i++) c = table[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
                return (c ^ 0xFFFFFFFF) >>> 0;
            };

            const _zipStore = (files) => {
                const enc = new TextEncoder();
                const bytes = [];
                const central = [];
                const writeU16 = (n) => { bytes.push(n & 255, (n >>> 8) & 255); };
                const writeU32 = (n) => { bytes.push(n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255); };
                const writeBytes = (arr) => { for (let i = 0; i < arr.length; i++) bytes.push(arr[i]); };

                files.forEach(({ name, data }) => {
                    const nameBytes = enc.encode(name);
                    const dataBytes = typeof data === 'string' ? enc.encode(data) : data;
                    const crc = _crc32(dataBytes);
                    const offset = bytes.length;
                    writeU32(0x04034b50);
                    writeU16(20); writeU16(0); writeU16(0); writeU16(0); writeU16(0);
                    writeU32(crc); writeU32(dataBytes.length); writeU32(dataBytes.length);
                    writeU16(nameBytes.length); writeU16(0);
                    writeBytes(nameBytes); writeBytes(dataBytes);
                    central.push({ nameBytes, crc, size: dataBytes.length, offset });
                });

                const cdStart = bytes.length;
                central.forEach(({ nameBytes, crc, size, offset }) => {
                    writeU32(0x02014b50);
                    writeU16(20); writeU16(20); writeU16(0); writeU16(0); writeU16(0); writeU16(0);
                    writeU32(crc); writeU32(size); writeU32(size);
                    writeU16(nameBytes.length); writeU16(0); writeU16(0);
                    writeU16(0); writeU16(0); writeU32(0);
                    writeU32(offset);
                    writeBytes(nameBytes);
                });
                const cdSize = bytes.length - cdStart;
                writeU32(0x06054b50);
                writeU16(0); writeU16(0);
                writeU16(central.length); writeU16(central.length);
                writeU32(cdSize); writeU32(cdStart);
                writeU16(0);

                return new Uint8Array(bytes);
            };

            const _buildXlsx = ({ headers, rows, sheetName }) => {
                const files = [];
                files.push({ name: '[Content_Types].xml', data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>' });
                files.push({ name: '_rels/.rels', data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' });
                files.push({ name: 'xl/workbook.xml', data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${_xmlEscape(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>` });
                files.push({ name: 'xl/_rels/workbook.xml.rels', data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>' });
                const styles = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="164" formatCode="[$$-2C0A]#,##0.00"/></numFmts><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>';
                files.push({ name: 'xl/styles.xml', data: styles });

                let sheet = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>';
                const allRows = [headers.map(h => ({ v: h, t: 's', s: 1 })), ...rows];
                allRows.forEach((cells, rIdx) => {
                    sheet += `<row r="${rIdx + 1}">`;
                    cells.forEach((cell, cIdx) => {
                        const ref = `${_colLetter(cIdx)}${rIdx + 1}`;
                        if (cell.t === 'n') {
                            sheet += `<c r="${ref}" s="${cell.s ?? 0}"><v>${cell.v}</v></c>`;
                        } else {
                            const styleAttr = cell.s ? ` s="${cell.s}"` : '';
                            sheet += `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${_xmlEscape(cell.v ?? '')}</t></is></c>`;
                        }
                    });
                    sheet += '</row>';
                });
                sheet += '</sheetData></worksheet>';
                files.push({ name: 'xl/worksheets/sheet1.xml', data: sheet });
                return _zipStore(files);
            };

            const num = (valor) => {
                const n = Number(valor);
                if (!Number.isFinite(n)) return 0;
                return Number(n.toFixed(2));
            };

            const headerRow = encabezados;

            const dataRows = [];
            let totalConveniado = 0;
            let totalAdenda = 0;

            for (const grupo of grupos) {
                let subConveniado = 0;
                let subAdenda = 0;

                for (const obra of grupo.obras) {
                    const conv = Number(obra?.monto_obra || 0);
                    const ad = Number(obra?.adendas || 0);
                    subConveniado += conv;
                    subAdenda += ad;

                    if (!soloSubtotales) {
                        dataRows.push([
                            { v: grupo.organizacion, t: 's' },
                            { v: contactosOngTexto(grupo.ong_contactos), t: 's' },
                            { v: grupo.ong_categoria || '', t: 's' },
                            { v: grupo.ong_terminal_politica || '', t: 's' },
                            { v: getExpedienteProyecto(obra), t: 's' },
                            { v: num(conv), t: 'n', s: 2 },
                            { v: num(ad), t: 'n', s: 2 },
                            { v: num(conv + ad), t: 'n', s: 2 },
                            { v: getTipoObra(obra).join(' / '), t: 's' },
                            { v: getUbicacion(obra), t: 's' },
                        ]);
                    }
                }

                const subTotal = subConveniado + subAdenda;
                const filaSub = [
                    { v: `Subtotal ${grupo.organizacion} (${grupo.obras.length} ${grupo.obras.length === 1 ? 'obra' : 'obras'})`, t: 's' },
                    { v: contactosOngTexto(grupo.ong_contactos), t: 's' },
                    { v: grupo.ong_categoria || '', t: 's' },
                    { v: grupo.ong_terminal_politica || '', t: 's' },
                ];
                if (!soloSubtotales) filaSub.push({ v: '', t: 's' });
                filaSub.push({ v: num(subConveniado), t: 'n', s: 2 });
                filaSub.push({ v: num(subAdenda), t: 'n', s: 2 });
                filaSub.push({ v: num(subTotal), t: 'n', s: 2 });
                if (!soloSubtotales) { filaSub.push({ v: '', t: 's' }); filaSub.push({ v: '', t: 's' }); }
                dataRows.push(filaSub);

                totalConveniado += subConveniado;
                totalAdenda += subAdenda;
            }

            const filaTot = [
                { v: 'Total general', t: 's', s: 1 },
            ];
            const colsAntesMontos = soloSubtotales ? 4 : 5;
            for (let i = 1; i < colsAntesMontos; i++) filaTot.push({ v: '', t: 's' });
            filaTot.push({ v: num(totalConveniado), t: 'n', s: 2 });
            filaTot.push({ v: num(totalAdenda), t: 'n', s: 2 });
            filaTot.push({ v: num(totalConveniado + totalAdenda), t: 'n', s: 2 });
            if (!soloSubtotales) { filaTot.push({ v: '', t: 's' }); filaTot.push({ v: '', t: 's' }); }
            dataRows.push(filaTot);

            const bytes = _buildXlsx({ headers: headerRow, rows: dataRows, sheetName: 'Informe' });
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${nombreArchivo}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al generar el XLS:', error);
        }
    };

    const grupos = useMemo(() => {
        const map = new Map();
        for (const obra of obras) {
            const key = String(obra?.ong_id ?? obra?.organizacion ?? 'sin-ong');
            if (!map.has(key)) {
                map.set(key, {
                    ong_id: obra?.ong_id,
                    organizacion: obra?.organizacion || '(sin nombre)',
                    ong_categoria: obra?.ong_categoria,
                    ong_terminal_politica: obra?.ong_terminal_politica,
                    ong_contactos: obra?.ong_contactos,
                    obras: [],
                });
            }
            map.get(key).obras.push(obra);
        }
        return Array.from(map.values());
    }, [obras]);

    const totalInversion = obras.reduce((acc, o) => acc + getMontoConveniado(o), 0);

    const puedeVer =
        role === 'admin'
        || role === 'registro_ongs'
        || (Array.isArray(roles) && roles.includes('registro_ongs'));

    if (!puedeVer) {
        return (
            <div className="p-8 text-center text-gray-500">
                Acceso denegado.
            </div>
        );
    }

    return (
        <div className="p-6 w-full max-w-full">
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Informe de Organizaciones
            </h1>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                    <label htmlFor="categoria" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Categoría
                    </label>
                    <select
                        id="categoria"
                        value={categoria}
                        onChange={(e) => setCategoria(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 min-w-[220px]"
                    >
                        <option value="">Todas las categorías</option>
                        {optionsCategoria.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label htmlFor="partido" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Partido (ubicación)
                    </label>
                    <select
                        id="partido"
                        value={partido}
                        onChange={(e) => setPartido(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 min-w-[200px]"
                    >
                        <option value="">Todos los partidos</option>
                        {optionsPartido.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col flex-1 min-w-[220px]">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Criterio de año
                    </label>
                    <Select
                        options={CRITERIOS_ANIO}
                        value={CRITERIOS_ANIO.find((c) => c.value === criterioYear)}
                        onChange={(opt) => setCriterioYear(opt?.value || 'pago_anticipo')}
                        isClearable={false}
                        className="text-sm"
                        styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                    />
                </div>

                <div className="flex flex-col flex-1 min-w-[200px]">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Año
                    </label>
                    <Select
                        isMulti
                        options={yearOptions}
                        value={selectedYearOptions}
                        onChange={handleYearsChange}
                        isClearable
                        placeholder="Todos los años..."
                        closeMenuOnSelect={false}
                        className="text-sm"
                        styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                    />
                </div>

                <div className="flex flex-col flex-1 min-w-[220px]">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Gestión
                    </label>
                    <Select
                        isMulti
                        options={gestionOptions}
                        value={selectedGestionOptions}
                        onChange={handleGestionChange}
                        isClearable
                        placeholder="Todas las gestiones..."
                        closeMenuOnSelect={false}
                        className="text-sm"
                        styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                    />
                </div>

                <div className="flex flex-col flex-1 min-w-[260px]">
                    <label htmlFor="busqueda" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Buscar organización
                    </label>
                    <input
                        id="busqueda"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') cargar({ categoria, partido, q: q.trim(), years, criterioYear });
                        }}
                        placeholder="Obispado, Cáritas, Arzobispado, iglesia, fundación..."
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    />
                </div>

                <button
                    onClick={() => cargar({ categoria, partido, q: q.trim(), years, criterioYear })}
                    disabled={cargando}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                    {cargando ? 'Generando...' : 'Generar informe'}
                </button>

                <button
                    type="button"
                    onClick={restablecerFiltros}
                    disabled={cargando}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                    Restablecer filtros
                </button>

                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 pb-2">
                    <input
                        type="checkbox"
                        checked={soloSubtotales}
                        onChange={(e) => setSoloSubtotales(e.target.checked)}
                        className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    Solo subtotales por ONG
                </label>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">{error}</div>
            )}

            {generado && obras.length > 0 && (
                <div className="mb-4 flex justify-end gap-3">
                    <button
                        onClick={handleDescargarXLS}
                        className="inline-flex items-center px-4 py-2 border border-teal-600 text-sm font-medium rounded-md shadow-sm text-teal-700 bg-white hover:bg-teal-50"
                    >
                        Descargar XLS
                    </button>
                    <button
                        onClick={handleDownloadPDF}
                        disabled={descargando}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                    >
                        {descargando ? 'Generando PDF...' : 'Descargar PDF'}
                    </button>
                </div>
            )}

            {generado && obras.length > 0 && (
                <div className="bg-white p-8 px-10 sm:p-12 shadow-xl rounded-lg border border-gray-200">
                    <div ref={pdfRef} className="font-sans">
                        <div className="mb-6" data-pdf-omit="true">
                            <img src={encabezadoImg} alt="Encabezado del informe" className="w-full h-auto" />
                        </div>

                        <div className="text-center mb-8">
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">
                                Organizaciones: {categoriaLabel}
                            </h2>
                            <h3 className="text-base font-medium text-gray-600 mt-1">
                                Informe de intervenciones complementarias del hábitat
                                {partidoLabel ? ` — partido: ${partidoLabel}` : ''}
                                {yearsLabel ? ` — años: ${yearsLabel}` : ''}
                                {q ? ` — búsqueda: "${q}"` : ''}
                                {soloSubtotales ? ' — solo subtotales por ONG' : ''}
                            </h3>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Inversión total
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearMonto(totalInversion)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Cantidad de obras
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(obras.length)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Organizaciones
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(grupos.length)}</p>
                            </div>
                        </div>

                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-teal-700 text-white">
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-64">
                                        Organización
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-80">
                                        Contacto ONG
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-28">
                                        Categoría
                                    </th>
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-36">
                                        Terminal Política
                                    </th>
                                    {!soloSubtotales && (
                                        <th className="border border-teal-800 px-3 py-2 text-left font-medium w-44">
                                            Exp. de Proyecto
                                        </th>
                                    )}
                                    <th className="border border-teal-800 px-3 py-2 text-left font-medium w-32">Monto</th>
                                    {!soloSubtotales && (
                                        <th className="border border-teal-800 px-3 py-2 text-left font-medium w-28">
                                            Tipo de obra
                                        </th>
                                    )}
                                    {!soloSubtotales && (
                                        <th className="border border-teal-800 px-3 py-2 text-left font-medium w-40">Ubicación</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {grupos.map((grupo) => {
                                    const subtotal = grupo.obras.reduce((acc, o) => acc + getMontoConveniado(o), 0);
                                    const lineasContacto = contactosOngPartes(grupo.ong_contactos);
                                    return (
                                        <Fragment key={grupo.ong_id ?? grupo.organizacion}>
                                            {!soloSubtotales && grupo.obras.map((obra) => {
                                                const adendas = Number(obra?.adendas || 0);
                                                const monto = Number(obra?.monto_obra || 0);
                                                const exp = String(obra?.id_partido_obra || '').trim();
                                                const nombre = String(obra?.nombre_obra || '').trim();
                                                return (
                                                    <tr key={obra?.id}>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            <div className="font-medium break-words">
                                                                {grupo.organizacion}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {exp}
                                                                {nombre ? ` · ${nombre}` : ''}
                                                            </div>
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 text-xs break-words">
                                                            {lineasContacto.length
                                                                ? lineasContacto.map((linea, i) => <div key={i}>{linea}</div>)
                                                                : '—'}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 text-xs">
                                                            {grupo.ong_categoria || '—'}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 text-xs">
                                                            {grupo.ong_terminal_politica || '—'}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 text-xs break-all">
                                                            {getExpedienteProyecto(obra)}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-right text-gray-800 whitespace-nowrap">
                                                            {adendas > 0 ? (
                                                                <>
                                                                    <span className="block">{formatearMonto(monto)}</span>
                                                                    <span className="block text-xs text-gray-600">
                                                                        + adenda por {formatearMonto(adendas)}
                                                                    </span>
                                                                    <span className="block text-xs font-medium text-gray-800">
                                                                        Total: {formatearMonto(monto + adendas)}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                formatearMonto(monto)
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            {getTipoObra(obra).map((linea) => (
                                                                <div key={linea}>{linea}</div>
                                                            ))}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                                            {getUbicacion(obra)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className={soloSubtotales ? '' : 'bg-teal-50'}>
                                                <td className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                                                    Subtotal {grupo.organizacion} ({grupo.obras.length}{' '}
                                                    {grupo.obras.length === 1 ? 'obra' : 'obras'})
                                                </td>
                                                <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 break-words">
                                                    {lineasContacto.length
                                                        ? lineasContacto.map((linea, i) => <div key={i}>{linea}</div>)
                                                        : '—'}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">
                                                    {grupo.ong_categoria || '—'}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">
                                                    {grupo.ong_terminal_politica || '—'}
                                                </td>
                                                {!soloSubtotales && (
                                                    <td className="border border-gray-300 px-3 py-2"></td>
                                                )}
                                                <td className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-800 whitespace-nowrap">
                                                    {formatearMonto(subtotal)}
                                                </td>
                                                {!soloSubtotales && (
                                                    <td className="border border-gray-300 px-3 py-2" colSpan={2}></td>
                                                )}
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-teal-700 text-white">
                                    <td className="border border-teal-800 px-3 py-2 text-right font-semibold" colSpan={soloSubtotales ? 4 : 5}>
                                        Total general
                                    </td>
                                    <td className="border border-teal-800 px-3 py-2 text-right font-semibold whitespace-nowrap">
                                        {formatearMonto(totalInversion)}
                                    </td>
                                    {!soloSubtotales && (
                                        <td className="border border-teal-800 px-3 py-2" colSpan={2}></td>
                                    )}
                                </tr>
                            </tfoot>
                        </table>

                        <div className="mt-8 mb-6 flex justify-between items-center text-sm text-gray-500">
                            <span>Dirección Provincial de Intervenciones Complementarias</span>
                            <span>Fecha de salida: {new Date().toLocaleDateString('es-AR')}</span>
                        </div>

                        <div className="mb-6" data-pdf-omit="true">
                            <img src={pieImg} alt="Pie de página del informe" className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InformeOrganizacionesPage;
